import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Button, IconButton, Typography, COLORS } from 'pvs-design-system';
import { Box, Stack, LinearProgress } from '@mui/material';
import Close from '@mui/icons-material/Close';

interface TourStep {
  id: string;
  title: string;
  body: string;
  /** id of DOM element to spotlight, or null for centered modal */
  targetId: string | null;
}

const MANAGE_STEPS: TourStep[] = [
  {
    id: 'welcome',
    title: 'Your Virtual Library',
    body: 'Everything your team needs, in one place. Browse folders, search resources, and keep track of what you visit most. You also have full access to organize and update the library.',
    targetId: null,
  },
  {
    id: 'search',
    title: 'Find anything, fast',
    body: 'Type a keyword and the library searches titles, descriptions, and folders at once. Use the field selector to narrow your search, or the settings icon to match exact phrases.',
    targetId: 'home-search-bar',
  },
  {
    id: 'browse',
    title: 'Browse by folder',
    body: 'Resources are organized into folders by topic. Click any folder to explore what is inside. Folders can be nested, so you can drill down to exactly what you need.',
    targetId: 'browse-section',
  },
  {
    id: 'resource-details',
    title: 'Open resources directly',
    body: 'Click any resource row to open it immediately in a new tab. You can also press Enter or Space on any focused row to open it.',
    targetId: null,
  },
  {
    id: 'history',
    title: 'Pick up where you left off',
    body: 'The library remembers your most recently opened resources and surfaces them in the "Suggested" section on the home page, so you can jump back to something you have already found.',
    targetId: null,
  },
  {
    id: 'finish',
    title: 'Ready to explore',
    body: 'You can reopen this tour any time by clicking "Guided Tour" in the top bar. Have a resource to add or a folder to update? Use the Add folder or Add resource button to get started.',
    targetId: null,
  },
];

const VIEW_STEPS: TourStep[] = [
  {
    id: 'welcome',
    title: 'Your Virtual Library',
    body: 'Everything you need, in one place. Browse folders, search for resources, and track what you visit most. This quick tour will show you how to find what you need.',
    targetId: null,
  },
  {
    id: 'search',
    title: 'Find anything, fast',
    body: 'Type a keyword and the library searches titles, descriptions, and folders at once. Use the field selector to narrow your search, or the settings icon to match exact phrases.',
    targetId: 'home-search-bar',
  },
  {
    id: 'browse',
    title: 'Browse by folder',
    body: 'Resources are organized into folders by topic. Click any folder to explore what is inside. Folders can be nested, so you can drill down to exactly what you need.',
    targetId: 'browse-section',
  },
  {
    id: 'resource-details',
    title: 'Open resources directly',
    body: 'Click any resource row to open it immediately in a new tab. You can also press Enter or Space on any focused row to open it.',
    targetId: null,
  },
  {
    id: 'history',
    title: 'Pick up where you left off',
    body: 'The library remembers your most recently opened resources and surfaces them in the "Suggested" section on the home page, so you can jump back to something you have already found.',
    targetId: null,
  },
  {
    id: 'finish',
    title: 'Ready to explore',
    body: 'You can reopen this tour any time by clicking "Guided Tour" in the top bar. Start with the search bar or browse a folder to find your first resource.',
    targetId: null,
  },
];

interface TourModalProps {
  /** Whether the user has permission to manage the library. */
  canManage: boolean;
  isOpen: boolean;
  /** Called when user dismisses early (×, Skip, backdrop click, Escape). */
  onSkip: () => void;
  /** Called when user completes the final step (Finish). */
  onFinish: () => void;
}

const CARD_W = 360;
const CARD_H_APPROX = 210;
const GAP = 14;

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const TourModal: React.FC<TourModalProps> = ({ canManage, isOpen, onSkip, onFinish }) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [spotRect, setSpotRect] = useState<DOMRect | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (isOpen) setStepIndex(0);
  }, [isOpen]);

  const steps = useMemo(() => (canManage ? MANAGE_STEPS : VIEW_STEPS), [canManage]);
  const current = steps[stepIndex];
  const isLast = stepIndex === steps.length - 1;

  // ── Focus management: save/restore trigger focus, move into dialog on open ──
  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      const timer = setTimeout(() => {
        const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
        focusable?.[0]?.focus();
      }, 50);
      return () => clearTimeout(timer);
    } else {
      previousFocusRef.current?.focus();
      previousFocusRef.current = null;
    }
  }, [isOpen]);

  // ── Keyboard: Escape closes, Tab traps focus inside dialog ──────────────
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onSkip();
        return;
      }
      if (e.key === 'Tab') {
        if (!dialogRef.current) return;
        const focusable = Array.from(
          dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onSkip]);

  const refreshRect = useCallback(() => {
    if (!isOpen) return;
    const step = steps[stepIndex];
    const targetId = step?.targetId;
    if (targetId) {
      const el = document.getElementById(targetId);
      if (el) {
        setSpotRect(el.getBoundingClientRect());
        return;
      }
    }
    setSpotRect(null);
  }, [isOpen, stepIndex, steps]);

  useEffect(() => {
    refreshRect();
    window.addEventListener('resize', refreshRect);
    window.addEventListener('scroll', refreshRect, true);
    return () => {
      window.removeEventListener('resize', refreshRect);
      window.removeEventListener('scroll', refreshRect, true);
    };
  }, [refreshRect]);

  // Click outside the tour card to dismiss.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      const card = document.getElementById('tour-card');
      if (card && !card.contains(e.target as Node)) {
        onSkip();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, onSkip]);

  if (!isOpen) return null;

  // ── Card positioning ─────────────────────────────────────────────────
  let cardStyle: React.CSSProperties = {
    position: 'fixed',
    width: CARD_W,
    zIndex: 1400,
    background: COLORS.white,
    borderRadius: 14,
    boxShadow: '0 12px 40px rgba(0,0,0,0.22)',
    padding: 24,
  };

  if (spotRect) {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    if (spotRect.right + GAP + CARD_W < vw) {
      cardStyle.left = spotRect.right + GAP;
      cardStyle.top = Math.max(GAP, Math.min(spotRect.top, vh - CARD_H_APPROX - GAP));
    } else if (spotRect.bottom + GAP + CARD_H_APPROX < vh) {
      cardStyle.left = Math.max(GAP, Math.min(spotRect.left, vw - CARD_W - GAP));
      cardStyle.top = spotRect.bottom + GAP;
    } else if (spotRect.top - GAP - CARD_H_APPROX > 0) {
      cardStyle.left = Math.max(GAP, Math.min(spotRect.left, vw - CARD_W - GAP));
      cardStyle.top = spotRect.top - GAP - CARD_H_APPROX;
    } else {
      cardStyle.top = '50%';
      cardStyle.left = '50%';
      cardStyle.transform = 'translate(-50%, -50%)';
    }
  } else {
    cardStyle.top = '50%';
    cardStyle.left = '50%';
    cardStyle.transform = 'translate(-50%, -50%)';
  }

  const PAD = 8;

  return (
    <>
      {/* ── SVG overlay (decorative, hidden from AT) ─────────────────── */}
      <svg
        aria-hidden="true"
        style={{
          position: 'fixed',
          inset: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 1300,
          pointerEvents: 'none',
        }}
      >
        {spotRect ? (
          <>
            <defs>
              <mask id="tour-spotlight-mask">
                <rect width="100%" height="100%" fill="white" />
                <rect
                  x={spotRect.left - PAD}
                  y={spotRect.top - PAD}
                  width={spotRect.width + PAD * 2}
                  height={spotRect.height + PAD * 2}
                  rx="10"
                  fill="black"
                />
              </mask>
            </defs>
            <rect
              width="100%"
              height="100%"
              fill="rgba(0,0,0,0.52)"
              mask="url(#tour-spotlight-mask)"
            />
            <rect
              x={spotRect.left - PAD}
              y={spotRect.top - PAD}
              width={spotRect.width + PAD * 2}
              height={spotRect.height + PAD * 2}
              rx="10"
              fill="none"
              stroke={COLORS.purple[700]}
              strokeWidth="2.5"
              strokeDasharray="8 4"
              opacity="0.9"
            />
          </>
        ) : (
          <rect width="100%" height="100%" fill="rgba(0,0,0,0.45)" />
        )}
      </svg>

      {/* ── Tour card ────────────────────────────────────────────────── */}
      <Box
        id="tour-card"
        ref={dialogRef}
        style={cardStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tour-title"
        aria-describedby="tour-body"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <IconButton
          size="small"
          color="secondary"
          onClick={onSkip}
          aria-label="Close tour"
          sx={{ position: 'absolute', right: 12, top: 12 }}
        >
          <Close fontSize="small" />
        </IconButton>

        {/* Content — aria-live announces the new step to screen readers on Next/Back,
            since focus intentionally stays on the nav buttons rather than jumping around. */}
        <Box sx={{ mb: 2.5, pr: 2 }} aria-live="polite" aria-atomic="true">
          <Typography
            variant="label-sm"
            style={{ display: 'block', color: COLORS.purple[700], marginBottom: 6, fontWeight: 600, letterSpacing: '0.04em' }}
          >
            Step {stepIndex + 1} of {steps.length}
          </Typography>
          <h2
            id="tour-title"
            style={{
              fontSize: 20,
              fontWeight: 700,
              color: COLORS.gray[900],
              margin: '0 0 12px',
              lineHeight: '28px',
            }}
          >
            {current.title}
          </h2>
          <Typography id="tour-body" variant="p-md" style={{ color: COLORS.gray[700], margin: 0 }}>
            {current.body}
          </Typography>
        </Box>

        {/* Footer buttons */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Button
            size="small"
            variant="text"
            onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
            disabled={stepIndex === 0}
            sx={{ textTransform: 'none', color: stepIndex === 0 ? COLORS.gray[200] : COLORS.gray[700], minWidth: 0, px: 1 }}
          >
            ‹ Back
          </Button>
          <Button
            size="small"
            variant="text"
            onClick={onSkip}
            sx={{ textTransform: 'none', color: COLORS.gray[700], minWidth: 0, px: 1 }}
          >
            Skip
          </Button>
          {isLast ? (
            <Button
              size="small"
              variant="contained"
              onClick={onFinish}
              sx={{ textTransform: 'none', minWidth: 80, background: COLORS.purple[700], '&:hover': { background: COLORS.purple[800] } }}
            >
              Finish
            </Button>
          ) : (
            <Button
              size="small"
              variant="contained"
              onClick={() => setStepIndex((i) => i + 1)}
              sx={{ textTransform: 'none', minWidth: 80, background: COLORS.purple[700], '&:hover': { background: COLORS.purple[800] } }}
            >
              Next ›
            </Button>
          )}
        </Stack>

        {/* Progress bar */}
        <LinearProgress
          variant="determinate"
          value={((stepIndex + 1) / steps.length) * 100}
          aria-label={`Tour progress: step ${stepIndex + 1} of ${steps.length}`}
          sx={{
            borderRadius: 999,
            height: 5,
            bgcolor: COLORS.gray[100],
            '& .MuiLinearProgress-bar': {
              background: COLORS.purple[700],
              borderRadius: 999,
            },
          }}
        />
      </Box>
    </>
  );
};

export default TourModal;
